import { MerchantProductType } from 'types/product';
import { FlattedCategoryType, MerchantType } from 'types/components/shared';
import { CategoryType } from 'types/components/categories';
import { characters } from 'utils/characters';

export const removeDuplicates = (arr: any[]) => {
    const uniqueSet = new Set(arr.map((obj) => JSON.stringify(obj)));
    return Array.from(uniqueSet).map((str) => JSON.parse(str));
};

export function hexIsLight(color: string) {
    const hex = color.replace('#', '');

    const cr = parseInt(hex.substring(0, 2), 16);
    const cg = parseInt(hex.substring(2, 4), 16);
    const cb = parseInt(hex.substring(4, 6), 16);

    const brightness = (cr * 299 + cg * 587 + cb * 114) / 1000;

    return brightness > 150;
}

export const filterCategories = (categories: CategoryType[] | undefined, searchText: string, isMatch?: boolean): CategoryType[] => {
    if (!categories) return [];
    if (!searchText) return categories;
    let filteredCategories: CategoryType[] = [];

    categories.forEach((item) => {
        const { children, ...rest } = item;
        const stringifiedCategory = JSON.stringify(rest).toLowerCase();

        if (isMatch) {
            if (rest.id.toString() === searchText) {
                filteredCategories.push(item);
            }
        } else {
            if (stringifiedCategory.includes(searchText.toLowerCase())) {
                filteredCategories.push(item);
            }
        }

        if (children?.length) {
            const filteredChildren = filterCategories(item.children, searchText, isMatch);
            filteredCategories = filteredCategories.concat(filteredChildren);
        }
    });

    return filteredCategories;
};

export const flatCategories = (categories: CategoryType[] | undefined): FlattedCategoryType[] => {
    if (!categories) return [];
    let toReturn: FlattedCategoryType[] = [];

    const flattedCat = (cat: CategoryType, level: number): FlattedCategoryType => ({
        id: cat.id,
        name: cat.name,
        title: cat.title,
        description: cat.description,
        hasChildren: Boolean(cat?.children?.length),
        level
    });

    const flatChild = (cat: CategoryType, level: number) => {
        const father = flattedCat(cat, level);

        toReturn = [...toReturn, father];

        if (cat.children) {
            cat.children.forEach((itemB) => {
                flatChild(itemB, level + 1);
            });
        }
    };

    categories.forEach((itemA) => {
        flatChild(itemA, 1);
    });

    return toReturn;
};

export function stringToColor(string: string) {
    let hash = 0;
    let y;

    /* eslint-disable no-bitwise */
    for (y = 0; y < string.length; y += 1) {
        hash = string.charCodeAt(y) + string.charCodeAt(y) + ((hash << 4) - hash);
    }

    let color = '';
    let i;

    for (i = 0; i < 3; i += 1) {
        let value = (hash >> (i * 8)) & 0xff;

        value = value > 0 ? value : string.length;

        color += `${(value + value * 10).toString(16)}`.replaceAll('.', '');
    }
    const colorString = `#${color.substring(0, 6)}`;

    /* eslint-enable no-bitwise */
    return colorString;
}

export const generateRandomString = (num: number) => {
    let result1 = '';
    const charactersLength = characters.length;
    for (let i = 0; i < num; i += 1) {
        result1 += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result1;
};

export const createMerchantList = (data: { [key: string]: any }): MerchantType[] => {
    let list: MerchantType[] = [];

    // add father
    const father: MerchantType = {
        name: data.name,
        status: data.status,
        merchantId: data.merchantId,
        isFather: true
    };

    data.multiCatalogMerchant.merchants.forEach((item: { [key: string]: any }) => {
        const newMerch: MerchantType = {
            name: item.name,
            status: item.status,
            merchantId: item.merchantId,
            isFather: false
        };

        list = [...list, newMerch];
    });

    return [father, ...list];
};

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function removeDuplicateMerchants(data: MerchantProductType[]): MerchantProductType[] {
    const seenMerchants: Record<number, boolean> = {};

    return data.filter((merchant) => {
        if (seenMerchants[merchant.merchantId]) {
            return false;
        }
        seenMerchants[merchant.merchantId] = true;
        return true;
    });
}

export const urlIsImage = (urlString: string) => {
    const imageRegex = /^https?:\/\/.*\.(png|jpg|jpeg|gif|bmp|svg)$/i;
    const isImageURL = imageRegex.test(urlString);

    return isImageURL;
};

const typeMap: { [key: string]: string } = {
    Usuarios: 'users',
    Perfiles: 'profiles',
    Productos: 'products',
    Categorías: 'categories',
    Marcas: 'brands',
    Facets: 'facets',
    Proveedores: 'vendors',
    'Actualización Masiva': 'massive_load',
    'Bitácora de Cambios': 'change_log',
    Detalle: 'health-content'
};

export const getNewRouteType = (type: string): string => typeMap[type] || '*';

export function removeProperty<T extends Record<string, any>, K extends keyof T>(obj: T, key: K): Omit<T, K> {
    const data = { ...obj };

    delete data[key];

    return { ...data };
}

export function expToken(token: string) {
    const base64Url = token.split('.')[1] || '';
    const base64WithPlus = base64Url.replace(/-/g, '+');
    const base64 = base64WithPlus.replace(/_/g, '/');

    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join('')
    );

    const parsed = JSON.parse(jsonPayload);

    return parsed.exp * 1000;
}

/**
 *
 * @param asyncFn Function
 *
 * Example: retryAsync(() => myFunc(param))
 *
 * Example: retryAsync(myFunc)
 *
 * @param maxAttempts
 * @default 2
 * @returns T
 */
export async function retryAsync<T>(asyncFn: () => Promise<T>, maxAttempts: number = 2): Promise<T> {
    let attempts = 0;
    let lastError: any;

    while (attempts < maxAttempts) {
        try {
            return await asyncFn();
        } catch (error) {
            lastError = error;
            attempts++;
        }
    }

    throw lastError;
}
